import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  alpha,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import {
  DownloadIcon,
  LeftArrowIcon,
  PdfIcon,
  RightArrowIcon,
} from 'src/v3/core/icons'
import { ShowNotesPDFBlock } from 'src/v3/classroom-v2/types'
import { fetchShowNotesPDFUrl } from 'src/v3/classroom-v2/utils/api'

// eslint-disable-next-line fp/no-mutation
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

export let PDFBlockViewer = ({
  courseID,
  block,
}: {
  courseID: string
  block: ShowNotesPDFBlock
}) => {
  let theme = useTheme()
  let [presignedUrl, setPresignedUrl] = useState<string | null>(null)
  let [hasError, setHasError] = useState(false)
  let [numPages, setNumPages] = useState(0)
  let [pageNumber, setPageNumber] = useState(1)
  let [isPageRendering, setIsPageRendering] = useState(false)
  let [containerHeight, setContainerHeight] = useState<number | null>(null)
  let containerRef = useRef<HTMLDivElement>(null)

  let isCurrentRef = useRef(true)

  useEffect(() => {
    // Reset everything derived from the previous URL — otherwise switching
    // blocks (or retrying) can briefly render a stale PDF, or an in-flight
    // fetch for a block the user has since navigated away from can resolve
    // and clobber the current one.
    // eslint-disable-next-line fp/no-mutation
    isCurrentRef.current = true
    setPresignedUrl(null)
    setHasError(false)
    setNumPages(0)
    setPageNumber(1)
    setContainerHeight(null)
    fetchShowNotesPDFUrl(courseID, block.id)
      .then((url) => {
        if (isCurrentRef.current) setPresignedUrl(url)
      })
      .catch(() => {
        if (isCurrentRef.current) setHasError(true)
      })
    return () => {
      // eslint-disable-next-line fp/no-mutation
      isCurrentRef.current = false
    }
  }, [courseID, block.id])

  let handlePageChange = (newPage: number) => {
    setIsPageRendering(true)
    setPageNumber(newPage)
  }

  let onPageRenderSuccess = () => {
    setIsPageRendering(false)
    if (containerRef.current && containerHeight === null) {
      setContainerHeight(containerRef.current.offsetHeight)
    }
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.common.white, 0.03),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PdfIcon sx={{ color: 'error.main' }} />
          {numPages > 0 && (
            <Typography variant="caption" color="text.secondary">
              {numPages} pages
            </Typography>
          )}
        </Box>
        <Button
          component="a"
          variant="text"
          size="small"
          href={presignedUrl ?? ''}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!presignedUrl}
          startIcon={<DownloadIcon fontSize="small" />}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          Download
        </Button>
      </Box>

      {hasError ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Something went wrong loading this PDF. Please try again later.
          </Alert>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            p: 3,
            bgcolor: 'common.black',
          }}
        >
          <Box
            ref={containerRef}
            sx={{
              position: 'relative',
              minHeight: containerHeight ? `${containerHeight}px` : 400,
            }}
          >
            {(isPageRendering || !presignedUrl) && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.common.black, 0.2),
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            {presignedUrl && !hasError && (
              <Document
                file={presignedUrl}
                suspense={false}
                onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
                onLoadError={() => setHasError(true)}
              >
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer
                  renderAnnotationLayer
                  onRenderSuccess={onPageRenderSuccess}
                />
              </Document>
            )}
          </Box>
        </Box>
      )}

      {numPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            aria-label="Previous page"
            size="small"
            onClick={() => handlePageChange(Math.max(1, pageNumber - 1))}
            disabled={pageNumber <= 1 || isPageRendering}
            sx={{ minWidth: 0, p: 0.5 }}
          >
            <LeftArrowIcon fontSize="small" />
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {pageNumber} of {numPages}
          </Typography>
          <Button
            aria-label="Next page"
            size="small"
            onClick={() => handlePageChange(Math.min(numPages, pageNumber + 1))}
            disabled={pageNumber >= numPages || isPageRendering}
            sx={{ minWidth: 0, p: 0.5 }}
          >
            <RightArrowIcon fontSize="small" />
          </Button>
        </Box>
      )}

      {block.caption && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2.5, py: 1.5, fontStyle: 'italic' }}
        >
          {block.caption}
        </Typography>
      )}
    </Box>
  )
}
